import get from 'lodash/get'
import ResponsiveImage from './ResponsiveImage'
import RichContent from './RichContent'
import Section from './Section'
import { theme } from '../styles/theme'
import { createUseStyles } from '../helpers/createStyles'
import { useState, useEffect, useRef, useCallback } from 'react'
import { formatPageTitle } from './Meta'
import Button from './Button'
import { useSession } from 'next-auth/react'
import Accordion from '../components/Accordion'
import DetailPicker from '../components/DetailPicker'
import { getAllTepData } from '../store/tepStore'
import Card from './Card'
import { useRouter } from 'next/router'
import { useScrollListener } from './SmoothScrollbar/useSmoothScrollbar'
import cn from 'classnames'
import { isBrowser } from '../lib/helpers'
export default function TEPProductPage ({ page, settings }) {
  const { status } = useSession()
  if (status === 'unauthenticated') {
    window.location.href = '/trade-exclusive-login'// Explicitly not using router.push to avoid an infinite loop bug
  }

  const styles = useStyles()
  const router = useRouter()
  const [isMobile, setIsMobile] = useState(true)

  // We cannot use sticky position because all of our ancestor elements are overflow: hidden.
  // We cannot use fixed position because we use transform: translate3d to animate the custom scrollbar.
  // So we manually calculate the position of the card on scroll.
  const stickyCardRef = useRef(null)
  const mainSectionRef = useRef(null)

  useScrollListener(
    useCallback(({ y }) => {
      if (!stickyCardRef.current || !mainSectionRef.current) return

      const mainSectionRect = mainSectionRef.current.getBoundingClientRect()
      const card = stickyCardRef.current

      // Calculate the bottom of mainSection relative to viewport
      const mainSectionBottom = mainSectionRect.bottom

      if (!isMobile && (mainSectionBottom <= window.innerHeight)) { // If we've scrolled past the bottom of mainSection, unstick the card
        const newY = (y - window.innerHeight) + mainSectionBottom// Calculate the position of the card relative to the bottom of mainSection
        card.style.position = 'sticky'
        card.style.transform = `translate3d(0, ${newY}px, 0)`
      } else if (isMobile) { // If we're on mobile, unstick the card
        card.style.position = 'static'
        card.style.transform = 'translate3d(0, 0, 0)'
      } else { // If we're on desktop, stick the card to the bottom of the parent
        card.style.position = 'sticky'
        card.style.transform = `translate3d(0, ${y}px, 0)`
      }
    }),
    true,
    true
  )

  const heroImage = get(page, ['tepLandingHeroImage'])
  const heroImageMobile = get(page, ['tepLandingHeroImageMobile'])

  const coloursSubtitle = get(page, ['tepLandingColoursSubtitle'])
  const coloursFooterTitle = get(page, ['tepLandingColoursFooterTitle'])
  const coloursFooterDescription = get(page, ['tepLandingColoursFooterDescription'])
  const coloursAddendum = get(page, ['tepLandingColoursAddendum'])

  const [isColoursExpanded, setColoursExpanded] = useState(!isMobile)
  const [isShapesExpanded, setShapesExpanded] = useState(!isMobile)
  const [isCornersExpanded, setCornersExpanded] = useState(!isMobile)
  const [isFinishesExpanded, setFinishesExpanded] = useState(!isMobile)
  const [isSizeExpanded, setSizeExpanded] = useState(!isMobile)
  const [isHeightExpanded, setHeightExpanded] = useState(!isMobile)
  const [isBackingExpanded, setBackingExpanded] = useState(!isMobile)

  // If the screen is resized, we need to update the default state of the accordions
  const handleResize = () => {
    setIsMobile(window.innerWidth < theme.breakpoints.values.md)
  }
  useEffect(() => {
    setColoursExpanded(!isMobile)
    setShapesExpanded(!isMobile)
    setCornersExpanded(!isMobile)
    setFinishesExpanded(!isMobile)
    setSizeExpanded(!isMobile)
    setHeightExpanded(!isMobile)
    setBackingExpanded(!isMobile)
  }, [isMobile])

  const shapesTitle = get(page, ['tepLandingShapesTitle'])
  const cornersTitle = get(page, ['tepLandingCornersTitle'])
  const finishesTitle = get(page, ['tepLandingFinishesTitle'])
  const sizeTitle = get(page, ['tepLandingSizeTitle'])
  const heightTitle = get(page, ['tepLandingHeightTitle'])
  const backingTitle = get(page, ['tepLandingBackingTitle'])

  const [queryParam, setQueryParam] = useState('')
  const [tepProducts, setTepProducts] = useState([])
  const [, setSelectedTepProductId] = useState(null)
  const [selectedTepProduct, setSelectedTepProduct] = useState(null)
  const [tepProductColors, setTepProductColors] = useState([])
  const [tepProductCorners, setTepProductCorners] = useState([])
  const [tepProductFinishes, setTepProductFinishes] = useState([])
  const [tepProductShapes, setTepProductShapes] = useState([])

  const [availableColors, setAvailableColors] = useState([])
  const [availableCorners, setAvailableCorners] = useState([])
  const [availableFinishes, setAvailableFinishes] = useState([])
  const [availableShapes, setAvailableShapes] = useState([])

  const [, setLoading] = useState(true)
  const [, setError] = useState(null)
  async function fetchTEPData () {
    try {
      setLoading(true)
      const result = await getAllTepData()
      console.log('TEP Data:', result)

      setTepProductColors(result.tepProductColors || [])
      setTepProductCorners(result.tepProductCorners || [])
      setTepProductFinishes(result.tepProductFinishes || [])
      setTepProductShapes(result.tepProductShapes || [])
      setTepProducts(result.tepProducts || [])

      setError(null)
    } catch (err) {
      setError(err.message)
      console.log('Error fetching TEP data:', err)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (tepProducts.length === 0) {
      fetchTEPData()
    }
    if (isBrowser) {
      window.addEventListener('resize', handleResize)
      handleResize()
    }
  }, [])

  useEffect(() => {
    const queryParam = get(router, ['query', 'product-id'])
    console.log('Query Param:', queryParam)
    setQueryParam(queryParam)
  }, [router])

  useEffect(() => {
    if (!router) return
    if (!tepProducts) {
      const retryFetch = setTimeout(() => {
        fetchTEPData()
      }, 2000)

      return () => clearTimeout(retryFetch)
    }

    if (queryParam) {
      const selectedProduct = tepProducts.find(product => product._id === queryParam)
      if (selectedProduct) {
        setSelectedTepProductId(queryParam)
        setSelectedTepProduct(selectedProduct)

        // Manually update the page Title to the product title
        if (selectedProduct.title && !page.title.includes(selectedProduct.title)) {
          const siteTitle = get(settings, ['siteTitle'])
          const newTitle = formatPageTitle(selectedProduct.title, siteTitle)
          document.title = newTitle
        }

        const availableColorIds = selectedProduct?.availableColors?.map(color => color._ref) || []
        const availableCornerIds = selectedProduct?.availableCorners?.map(corner => corner._ref) || []
        const availableFinishIds = selectedProduct?.availableFinishes?.map(finish => finish._ref) || []
        const availableShapeIds = selectedProduct?.availableShapes?.map(shape => shape._ref) || []
        const availableColors = selectedProduct?.showAllColors ? tepProductColors : tepProductColors.filter(color => availableColorIds.includes(color._id))
        const availableCorners = tepProductCorners.filter(corner => availableCornerIds.includes(corner._id))
        const availableFinishes = tepProductFinishes.filter(finish => availableFinishIds.includes(finish._id))
        const availableShapes = tepProductShapes.filter(shape => availableShapeIds.includes(shape._id))
        setAvailableColors(availableColors)
        setAvailableCorners(availableCorners)
        setAvailableFinishes(availableFinishes)
        setAvailableShapes(availableShapes)

        console.log('availableColorIds:', availableColorIds)
        console.log('availableCornerIds:', availableCornerIds)
        console.log('availableFinishIds:', availableFinishIds)
        console.log('availableShapeIds:', availableShapeIds)

        console.log('tepProductColors:', tepProductColors)
        console.log('tepProductCorners:', tepProductCorners)
        console.log('tepProductFinishes:', tepProductFinishes)
        console.log('tepProductShapes:', tepProductShapes)

        console.log('Selected Product:', selectedProduct)
        console.log('Available Colors:', availableColors)
        console.log('Available Corners:', availableCorners)
        console.log('Available Finishes:', availableFinishes)
        console.log('Available Shapes:', availableShapes)
      } else {
        console.log('Product not found in tepProducts:', queryParam)
        setSelectedTepProductId(null)
        setSelectedTepProduct(null)
      }
    }
  }, [tepProducts, queryParam])

  if (status === 'loading' || !selectedTepProduct) {
    return <p>Loading...</p>
  }

  if (status === 'unauthenticated') {
    return (
      <Section className={styles.section} fullWidth>
        {/* Hero */}
        <div className={styles.tep_landing_hero}>
          <div className={styles.heroContainer}>
            <ResponsiveImage image={heroImage} mobileImage={heroImageMobile} className={styles.heroImage} />
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>Access Denied</h1>
              <RichContent className={styles.heroContent} content='Please login to access this restricted page' />
              <Button to='trade-exclusive-login' className={styles.darkenedButton}>Log In</Button>
              <Button to='trade-registration-form' className={styles.darkenedButton}>Sign Up</Button>
            </div>
          </div>
        </div>
      </Section>
    )
  }

  return (
    <Section ref={mainSectionRef} className={styles.mainSection} fullWidth>
      <div ref={stickyCardRef} className={`${styles.productCardContainer} productCardContainer`}>
        <Card cardContent={selectedTepProduct} isFocused hideFooter hideForeground disableHover aspectRatio='850/950' className={`${styles.productCard} productCard fullWidth`} />
      </div>
      <div className={styles.productDetailsSection}>
        <div>

          <div className={`${styles.productCardFront} productCardFront`}>
            <Card cardContent={selectedTepProduct} className={cn(styles.productCardFront, 'fullWidth overflow')} hideBackground hideViewProductButton hideFooterTitle disableHover rightAlignFooter aspectRatio='auto' />
          </div>

          {/* COLOURS */}
          {selectedTepProduct.showColorsSection && (

            <Accordion
              className={['tepAccordion', 'shade1', styles.accordionContainer]}
              label={selectedTepProduct.colorsSectionTitle}
              expanded={isColoursExpanded}
              setExpanded={setColoursExpanded}
            >
              {selectedTepProduct.colorsSectionDescription && selectedTepProduct.colorsSectionDescription !== '' &&
                <RichContent content={selectedTepProduct.colorsSectionDescription} className={cn(availableColors && availableColors.length > 0 ? styles.bottomPadding8 : '')} />}
              {availableColors && availableColors.length > 0 &&
                <DetailPicker
                  items={availableColors}
                  className={cn(styles.detailPickerContainer)}
                  hideDetailCard
                  defaultItem={{
                    subtitle: coloursSubtitle,
                    description: selectedTepProduct.colorsSectionDescription,
                    footerTitle: coloursFooterTitle,
                    footerDescription: coloursFooterDescription,
                    addendum: coloursAddendum,
                    itemName: 'COLOUR',
                    selectedSubtitle: 'COLOUR NAME',
                    selectedFooterTitle: 'Colour Characteristics:'
                  }}
                />}
            </Accordion>
          )}

          {/* SHAPES  */}
          {selectedTepProduct.showShapesSection && (
            <Accordion
              className={['tepAccordion', 'shade1', styles.accordionContainer]}
              label={shapesTitle}
              expanded={isShapesExpanded}
              setExpanded={setShapesExpanded}
            >
              <RichContent content={selectedTepProduct.shapesSectionDescription} className={cn(styles.bottomPadding8)} />

              <div className={styles.flexRow}>
                {availableShapes.map((shape, index) => (
                  // <pre>{JSON.stringify(shape, null, 2)}</pre>
                  <div key={index} className={styles.itemImage}>
                    <ResponsiveImage image={shape.image} />
                    <p className={styles.itemName}>{shape.name}</p>
                  </div>
                ))}
              </div>
            </Accordion>
          )}

          {/* CORNERS */}
          {selectedTepProduct.showCornersSection && (
            <Accordion
              className={['tepAccordion', 'shade1', styles.accordionContainer]}
              label={cornersTitle}
              expanded={isCornersExpanded}
              setExpanded={setCornersExpanded}
            >

              <RichContent content={selectedTepProduct.cornersSectionDescription} className={cn(styles.bottomPadding8)} />

              <div className={styles.flexRow}>
                {availableCorners.map((corner, index) => (
                  // <pre>{JSON.stringify(corner, null, 2)}</pre>
                  <div key={index} className={styles.itemImage}>
                    <ResponsiveImage image={corner.image} />
                    <p className={styles.itemName}>{corner.name}</p>
                  </div>
                ))}
              </div>
            </Accordion>
          )}

          {/* FINISHES */}
          {selectedTepProduct.showFinishesSection && (
            <Accordion
              className={['tepAccordion', 'shade1', styles.accordionContainer]}
              label={finishesTitle}
              expanded={isFinishesExpanded}
              setExpanded={setFinishesExpanded}
            >

              <RichContent content={selectedTepProduct.finishesSectionDescription} className={cn(styles.bottomPadding8)} />

              <div className={styles.flexRow}>
                {availableFinishes.map((finish, index) => (
                  // <pre>{JSON.stringify(finish, null, 2)}</pre>
                  <div key={index} className={styles.itemImage}>
                    <ResponsiveImage image={finish.image} />
                    <p className={styles.itemName}>{finish.name}</p>
                  </div>
                ))}
              </div>
            </Accordion>
          )}

          {/* BACKING */}
          {selectedTepProduct.showBackingSection && (
            <Accordion
              className={['tepAccordion', 'shade1', styles.accordionContainer]}
              label={backingTitle}
              expanded={isBackingExpanded}
              setExpanded={setBackingExpanded}
            >

              <div className={styles.contentList}>
                <RichContent content={selectedTepProduct.backingSectionDescription} />
              </div>
              <div className={styles.contentList}>
                {selectedTepProduct.backingSectionContent?.map((item, index) => (
                  <RichContent key={index} content={item.value} />
                ))}
              </div>

            </Accordion>
          )}

          {/* DIMENSIONS */}
          {selectedTepProduct.showSizeSection && (
            <Accordion
              className={['tepAccordion', 'shade1', styles.accordionContainer]}
              label={sizeTitle}
              expanded={isSizeExpanded}
              setExpanded={setSizeExpanded}
            >
              <div className={styles.contentList}>
                <RichContent content={selectedTepProduct.sizeSectionDescription} />
              </div>
              <div className={styles.contentList}>
                {selectedTepProduct.sizeSectionContent?.map((item, index) => (
                  <RichContent key={index} content={item.value} />
                ))}
              </div>
            </Accordion>
          )}

          {/* HEIGHT */}
          {selectedTepProduct.showHeightSection && (
            <Accordion
              className={['tepAccordion', 'shade1', styles.accordionContainer]}
              label={heightTitle}
              expanded={isHeightExpanded}
              setExpanded={setHeightExpanded}
            >
              <div className={styles.contentList}>
                <RichContent content={selectedTepProduct.heightSectionDescription} />
              </div>
              <div className={styles.contentList}>
                {selectedTepProduct.heightSectionContent?.map((item, index) => (
                  <RichContent key={index} content={item.value} />
                ))}
              </div>
            </Accordion>
          )}
        </div>

      </div>
    </Section>
  )
}
const useStyles = createUseStyles({
  topPadding8: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(8)
    }
  },
  bottomPadding8: {
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      paddingBottom: theme.spacing(8)
    }
  },

  flexRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      '& > *': {
        flex: '0 0 calc(50% - 12px)' // 2 items per row with spacing
      }
    },
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(2),
      '& > *': {
        flex: '0 0 calc(25% - 16px)' // 4 items per row with spacing
      }
    }
  },

  flexRowText: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    flex: '0 0 calc(50% - 12px)' // 2 items per row with spacing
  },

  flexCol: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  detailPickerContainer: {
    padding: 0,
    '& > div': {
      padding: 0
    }
  },

  accordionContainer: {
    '& .accordionContent': {
      border: 'none',
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
      '&.expanded': {
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
        border: 'none'

      },

      [theme.breakpoints.up('md')]: {
        paddingLeft: `${theme.spacing(4)}px !important`,
        paddingRight: `${theme.spacing(4)}px !important`,
        border: 'none',
        '&.expanded': {
          border: 'none',
          paddingLeft: `${theme.spacing(4)}px !important`,
          paddingRight: `${theme.spacing(4)}px !important`
        }
      }
    },
    '& .accordionBtn': {
      borderTop: '1px solid black',
      paddingLeft: '0 !important',
      paddingRight: '0 !important',

      [theme.breakpoints.up('md')]: {
        paddingLeft: `${theme.spacing(4)}px !important`,
        paddingRight: `${theme.spacing(4)}px !important`,
        '& > span': {
          justifyContent: 'flex-start'
        }
      }
    }
  },

  mainSection: {
    paddingTop: theme.spacing(16),

    overflow: 'hidden',
    // height: '100vh',
    // border: '5px solid blue',

    backgroundColor: '#F8F7EE',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0),
    justifyContent: 'flex-start',
    width: '100%',
    height: 'fit-content',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      // gap: theme.spacing(4),
      justifyContent: 'space-between',
      '& > div': {
        flex: 1
      }
    },

    '& .fullWidth': {
      width: '100% !important',
      minWidth: '100% !important',
      maxWidth: '100% !important'
    }

  },
  productCardContainer: {
    position: 'static',
    [theme.breakpoints.down('md')]: {
      // height: 'fit-content',
      // minWidth: '100vw'
    },
    marginTop: theme.spacing(-8),
    '& > div': {
      '& > div': {
        paddingTop: theme.spacing(24)
      }
    },
    [theme.breakpoints.up('md')]: {

      marginTop: theme.spacing(-16),
      maxHeight: '100vh',
      '& > div': {
        height: '100%',
        '& > div': {
          height: '100%'
        }
      }
    }
  },
  productCard: {

    // border: '5px solid red',

    [theme.breakpoints.down('md')]: {
      position: 'static',
      pointerEvents: 'none',
      marginTop: '-100px',
      '& .cardBackground': {
        paddingTop: '100px'

      }
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '100%',
      minWidth: '50vw',
      // position: 'fixed',
      top: '0'
    },

    '& .cardBackground': {
      // marginTop: '-100px',
      paddingTop: '100px',
      [theme.breakpoints.up('md')]: {
        paddingTop: '50px',
        justifyContent: 'flex-end'
        // justifyContent: 'flex-start',
        // justifyContent: 'space-between',
      }
    },

    '& .cardBackgroundHeader': {
      paddingTop: '92px',
      [theme.breakpoints.up('md')]: {
        paddingBottom: theme.spacing(12),
        paddingTop: theme.spacing(12)
      },
      '& h3': {
        display: 'none'
      },
      '& h4': {
        fontSize: 64,
        [theme.breakpoints.up('md')]: {
          fontSize: 96
        }
      },
      '& p': {
        fontSize: 14,
        [theme.breakpoints.up('md')]: {
          fontSize: 16
        }
      }
    },

    '& .cardBackgroundFooter': {
      '& h3': {
        fontSize: 10,
        [theme.breakpoints.up('md')]: {
          fontSize: 12
        }
      },
      '& p': {
        fontSize: 12,
        [theme.breakpoints.up('md')]: {
          fontSize: 14
        }
      }
    }

  },
  productCardFront: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    minWidth: '100%',
    maxWidth: '100%',
    minHeight: 'none',
    maxHeight: 'fit-content',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    '& img': {
      width: '100%'
    },
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(0),
      marginBottom: theme.spacing(8),
      aspectRatio: '1600/1000 !important',
      '& .imageContainer': {
        aspectRatio: '1600/1000 !important'
      }
    }
  },
  productDetailsSection: {
    width: '100%',
    height: '100%',
    minHeight: '70vh',
    '& > div': {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4)
    }
  },

  itemImage: {
    width: '100%',
    // height: 'fit-content',
    aspectRatio: '150/200'

  },

  pageTitle: {
    color: theme.colors.black,
    fontFamily: 'Iskry',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 700,
    letterSpacing: '8px',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(8)
  },

  heroContainer: {
    borderTop: '1px solid black',
    padding: theme.spacing(8),
    paddingBottom: theme.spacing(0)
  },

  heroTitle: {
    color: theme.colors.black,
    fontFamily: 'Iskry',
    fontStyle: 'normal',
    fontWeight: 700,
    width: '100%',
    minWidth: '50%',
    ' & p, div, h1, h2, h3, h4, h5, h6': {
      width: '100%',
      minWidth: '100%',
      fontSize: 28,
      [theme.breakpoints.up('md')]: {
        fontSize: 48
      }
    }
  },

  heroImage: {
    width: '100vw !important',
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(-8)
  },

  introImage: {
    width: '100vw !important',
    marginLeft: theme.spacing(-12)
  },

  nestedImage: {
    width: '100vw !important',
    marginLeft: theme.spacing(-4),
    marginBottom: theme.spacing(-4),

    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(-12),
      marginBottom: theme.spacing(-12)
    }
  },
  marginTop: {
    marginTop: theme.spacing(4)
  },

  contentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'space-between',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      gap: theme.spacing(38)
    },
    p: {
      fontSize: 14
    },
    '& > :nth-child(1)': {
      fontFamily: 'Iskry',
      fontSize: 20
    },
    '& > *': {
      [theme.breakpoints.up('md')]: {
        flex: 1
      }
    }

  },

  introText: {
    p: {
      fontFamily: 'Sohne',
      fontWeight: 300,
      fontSize: 14,
      [theme.breakpoints.up('md')]: {
        fontSize: 24
      }
    },

    ul: {
      listStyleType: 'none',
      paddingLeft: 0,
      h2: {
        width: '100%',
        fontFamily: 'Iskry',
        fontSize: 24,
        fontWeight: 700,
        display: 'table-header-group',
        [theme.breakpoints.up('md')]: {
          fontSize: 50
        },
        h3: {
          fontSize: 26,
          textWrapMode: 'nowrap',
          li: {
            fontFamily: 'Sohne',
            textWrapMode: 'wrap',
            paddingTop: theme.spacing(2),
            fontSize: 14
          }
        }
      }
    },
    '& div > ul:first-of-type': { // Only target the first ul
      counterReset: 'introListCounter',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        gap: theme.spacing(4),
        flexWrap: 'wrap',
        justifyContent: 'center'
      },
      [theme.breakpoints.up('xl')]: {
        gap: theme.spacing(16)
      },

      '> li': { // Only target direct children
        [theme.breakpoints.up('md')]: {
          display: 'flex',
          flexBasis: '48%'
        },
        [theme.breakpoints.up('lg')]: {
          flexBasis: '0%'
        },
        counterIncrement: 'introListCounter',
        paddingBottom: theme.spacing(2),
        '&::before': {
          content: 'counter(introListCounter, decimal-leading-zero)',
          marginRight: theme.spacing(2),
          fontFamily: 'Söhne Leicht', // todo make this into sohne breit -- needs to be imported
          fontWeight: 400,
          fontSize: 12
        }
      }
    }
  },

  enquireButton: {
    fontFamily: 'Sohne',
    fontWeight: 400,
    fontSize: 14,
    borderRadius: 90,
    border: '1px solid black',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    '&:hover': {
      backgroundColor: '#AFAC9C',
      color: theme.colors.white
    }
  },

  '#logo': {
    color: 'black !important'
  },

  itemName: {
    paddingTop: theme.spacing(1.5),
    fontSize: 12,
    color: '#333'
  }

})
